/*
 * Keyword search inside the current collection (snippets/otc-collection-search.liquid).
 * Asks the store search for the keyword, limited to this collection's product types, reads the
 * product cards from the search page's grid (same card blocks as the collection grid) and shows
 * them in place of the collection grid. Clearing the box restores the collection grid.
 */
const DELAY = 300;
const MIN_CHARS = 2;

/** One shared state for the desktop and mobile copies of the box. */
const state = { terms: '', page: 1, controller: null, timer: 0, saved: null };

function boxes() {
  return [...document.querySelectorAll('otc-coll-search')];
}

function gridParts() {
  const grid = document.querySelector('results-list .product-grid');
  if (!grid) return null;
  return { grid, wrap: grid.parentElement, count: document.querySelectorAll('.products-count-wrapper [role="status"]') };
}

function scopedQuery(terms, types) {
  const clean = terms.replace(/["()]/g, ' ').trim();
  if (!types.length) return clean;
  const scope = types.map((t) => `product_type:"${t.replace(/"/g, '')}"`).join(' OR ');
  return `${clean} AND (${scope})`;
}

class OtcCollSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.clearButton = this.querySelector('.otc-coll-search__clear');
    try {
      this.types = JSON.parse(this.dataset.types || '[]').filter(Boolean);
    } catch {
      this.types = [];
    }
    // Changing a filter re-renders the grid and this box: the search starts over on the new grid
    state.controller?.abort();
    Object.assign(state, { terms: '', page: 1, saved: null });
    this.clearButton.hidden = true;

    this.input.addEventListener('input', () => this.#onInput());
    this.querySelector('form').addEventListener('submit', (event) => {
      event.preventDefault();
      clearTimeout(state.timer);
      this.#search(1);
    });
    this.clearButton.addEventListener('click', () => {
      this.#mirror('');
      this.#restore();
      this.input.focus();
    });
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.input.value) {
        event.preventDefault();
        this.#mirror('');
        this.#restore();
      }
    });
  }

  #mirror(value) {
    for (const box of boxes()) {
      if (box.input && box.input.value !== value) box.input.value = value;
      if (box.clearButton) box.clearButton.hidden = !value;
    }
  }

  #onInput() {
    const value = this.input.value;
    this.#mirror(value);
    clearTimeout(state.timer);
    if (value.trim().length < MIN_CHARS) {
      this.#restore();
      return;
    }
    state.timer = setTimeout(() => this.#search(1), DELAY);
  }

  #label(key, terms, count) {
    return (this.dataset[key] || '').replace('[terms]', terms).replace('[count]', count);
  }

  async #search(page) {
    const terms = this.input.value.trim();
    if (terms.length < MIN_CHARS) return this.#restore();
    const parts = gridParts();
    if (!parts) return;

    state.controller?.abort();
    state.controller = new AbortController();
    state.terms = terms;
    state.page = page;

    const url = new URL(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}search` : '/search', location.origin);
    url.searchParams.set('q', scopedQuery(terms, this.types));
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    if (page > 1) url.searchParams.set('page', String(page));

    this.#save(parts);
    parts.wrap.setAttribute('aria-busy', 'true');
    let html;
    try {
      const response = await fetch(url, { signal: state.controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      html = await response.text();
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.#show(parts, [], this.#label('labelError'), false);
      return;
    } finally {
      parts.wrap.removeAttribute('aria-busy');
    }
    if (state.terms !== terms) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const items = [...doc.querySelectorAll('.product-grid > li.product-grid__item')];
    const countText = doc.querySelector('.products-count-wrapper [role="status"]')?.textContent || '';
    const total = parseInt(countText.replace(/[^\d]/g, ''), 10) || items.length;
    const hasMore = doc.querySelector('.product-grid')?.dataset.lastPage > page;

    const status =
      total === 0
        ? this.#label('labelNone', terms)
        : total === 1
          ? this.#label('labelOne', terms)
          : this.#label('labelResults', terms, total.toLocaleString());
    this.#show(parts, items, status, hasMore, page > 1);
  }

  #save(parts) {
    if (state.saved) return;
    state.saved = {
      hidden: [...parts.wrap.children].filter((el) => !el.classList.contains('otc-coll-search-results')),
      counts: [...parts.count].map((el) => el.textContent),
    };
  }

  #show(parts, items, status, hasMore, append = false) {
    let results = parts.wrap.querySelector('.otc-coll-search-results');
    if (!results) {
      results = document.createElement('div');
      results.className = 'otc-coll-search-results';
      const list = parts.grid.cloneNode(false);
      list.removeAttribute('ref');
      results.append(list);
      const more = document.createElement('button');
      more.type = 'button';
      more.className = 'button-secondary otc-coll-search-results__more';
      more.textContent = this.dataset.labelMore || 'Show more';
      more.addEventListener('click', () => this.#search(state.page + 1));
      results.append(more);
      parts.wrap.append(results);
    }
    for (const el of state.saved.hidden) el.style.display = 'none';
    const list = results.querySelector('.product-grid');
    if (!append) list.replaceChildren();
    list.append(...items.map((li) => document.importNode(li, true)));
    results.querySelector('.otc-coll-search-results__more').hidden = !hasMore;
    for (const el of parts.count) el.textContent = status;
  }

  #restore() {
    state.controller?.abort();
    state.terms = '';
    state.page = 1;
    if (!state.saved) return;
    const parts = gridParts();
    parts?.wrap.querySelector('.otc-coll-search-results')?.remove();
    for (const el of state.saved.hidden) el.style.display = '';
    if (parts) [...parts.count].forEach((el, i) => (el.textContent = state.saved.counts[i] ?? el.textContent));
    state.saved = null;
  }
}

if (!customElements.get('otc-coll-search')) customElements.define('otc-coll-search', OtcCollSearch);
